import { Component, inject, OnInit } from '@angular/core';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { CarpentryService } from 'src/app/services/configurators-2d/applications/carpentry.service';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { ModalService } from 'src/app/services/modal.service';
import { InstallationState, PricingState } from 'src/app/stores/tunnel/model/tunnel-data-state';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { CarpentryConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/carpentry-configuration-data';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-casto-carpentry-tunnel-installation',
  templateUrl: './carpentry-tunnel-installation.component.html',
  styleUrl: './carpentry-tunnel-installation.component.scss',
})
export class CarpentryTunnelInstallationComponent extends BaseTunnelObject implements OnInit {
  // Services
  protected carpentryService = inject(CarpentryService);
  protected modalService = inject(ModalService);
  protected featureSettingsService = inject(FeatureSettingsService);

  // Properties
  protected currentInstallation: InstallationState = {
    withInstallation: true,
    more2Years: undefined,
    detailInstallation: undefined,
  };
  private carpentryConfiguration: CarpentryConfigurationData;
  protected currentPricing: PricingState;
  protected loading = false;
  protected pricingLoading: boolean = false;
  protected more2Years: boolean = undefined;
  protected more2YearsOptions: any[] = [
    { label: 'yes', value: true },
    { label: 'no', value: false },
  ];
  private initInstallationLoading = false;

  constructor() {
    const tunnelNavigationStore = inject(TunnelNavigationStore);
    const tunnelDataStore = inject(TunnelDataStore);
    super(tunnelNavigationStore, tunnelDataStore);

    this.tunnelNavigationStore = tunnelNavigationStore;
    this.tunnelDataStore = tunnelDataStore;
  }

  async ngOnInit(): Promise<void> {
    if (!(await this.featureSettingsService.isPoseEnabled())) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    super.ngOnInit();
    await this.sendAccessPoseStatistic();
  }

  private async initInstallation() {
    if (this.initInstallationLoading) {
      return;
    }

    try {
      this.initInstallationLoading = true;
      const poseEnabled = await this.featureSettingsService.isPoseEnabled();
      await this.setWithInstallation(poseEnabled, true);
    } catch (error) {
      logError(error);
    } finally {
      this.initInstallationLoading = false;
    }
  }

  async sendAccessPoseStatistic() {
    //Envoi de la stat d'accès à la page de simulation de pose
    //   this.statisticService.sendStatistic(
    //     StatisticTypeEnum.KITCHEN_INSTALLATION_COUNT,
    //     1,
    //     CONFIG_CONTEXT.CARPENTRY,
    //     this.carpentryConfiguration.configurationId
    //   );
  }

  private shouldComputeInstallation(newCurrentInstallation: InstallationState): boolean {
    return (
      newCurrentInstallation.withInstallation != this.currentInstallation?.withInstallation &&
      !this.loading &&
      !(
        this.currentInstallation.detailInstallation === undefined &&
        newCurrentInstallation.detailInstallation !== undefined
      )
    );
  }

  protected subscribeToObservable() {
    this.subscriptionList.push(
      this.tunnelDataStore.getCurrentInstallation().subscribe(async currentInstallation => {
        if (currentInstallation) {
          const poseEnabled = await this.featureSettingsService.isPoseEnabled();

          if (!poseEnabled && currentInstallation.withInstallation !== false) {
            await this.setWithInstallation(false, true);
            return;
          }
          if (this.shouldComputeInstallation(currentInstallation)) {
            await this.computeInstallation();
          }
          if (!this.loading) {
            this.more2Years = currentInstallation.more2Years;
            this.currentInstallation.withInstallation = currentInstallation.withInstallation;
            this.currentInstallation.more2Years = currentInstallation.more2Years;
          }
          this.currentInstallation.detailInstallation = currentInstallation.detailInstallation;
        } else {
          await this.initInstallation();
        }
      }),
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        if (currentPricing) {
          this.currentPricing = currentPricing;
        }
      }),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.carpentryConfiguration = configurationData.get(CONFIG_CONTEXT.CARPENTRY) as CarpentryConfigurationData;
        }
      }),
      this.tunnelDataStore.getPricingLoading().subscribe(pricingLoading => {
        this.pricingLoading = pricingLoading;
      })
    );
  }

  private async computeInstallation() {
    try {
      this.loading = true;
      await this.carpentryService.computeInstallation();
    } catch (error) {
      logError(error);
    } finally {
      this.loading = false;
    }
  }

  protected async setMore2Years(more2Years: boolean): Promise<void> {
    if (more2Years === null) {
      return;
    }
    this.loading = true;
    this.currentInstallation.more2Years = more2Years;
    this.tunnelDataStore.setMoreThan2Years(more2Years);
    this.tunnelDataStore.resetCurrentFunding();
    await this.updateInstallation();
    this.loading = false;
  }

  private async updateInstallation() {
    await this.computeInstallation();
  }

  protected async setWithInstallation(withInstallation: boolean, force: boolean = false): Promise<void> {
    if (force || this.currentInstallation.withInstallation !== withInstallation) {
      this.loading = true;
      this.currentInstallation.withInstallation = withInstallation;
      this.tunnelDataStore.setWithInstallation(withInstallation);
      this.tunnelDataStore.setMoreThan2Years(this.more2Years);
      this.tunnelDataStore.resetCurrentFunding();
      await this.carpentryService.updateWithInstallation(withInstallation);
      await this.computeInstallation();
      this.loading = false;
    }
  }

  openDetail() {
    // this.modalService.display(MODAL.INSTALLATION_DETAIL, {
    //   installationDetails: this.currentInstallation.detailInstallation,
    //   bom: this.carpentryConfiguration?.products,
    // });
  }
}
